import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import InfoTooltipButton from "./InfoTooltipButton";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";

import useCustomStyles from "../../customAssets/customStyles";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import CardHeader from "../../components/Card/CardHeader";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "../../components/Card/CardFooter";
import CardText from "../../components/Card/CardText.js";

import { useTranslation } from "react-i18next";

/**
 * Estilos responsive para el bloque de certificados.
 *
 * Objetivos:
 * - evitar iconos/tooltip posicionados en absoluto
 * - dejar el icono dentro de la pastilla visual del header
 * - apilar columnas en móvil
 * - permitir wrap correcto de cadenas largas
 * - mejorar separación visual entre findings
 */
const useStyles = makeStyles((theme) => ({
  /**
   * La pastilla visual real del header es CardText.
   * El título y el icono deben vivir dentro.
   */
  headerBadge: {
    display: "block",
    width: "fit-content",
    maxWidth: "100%",
  },

  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
    maxWidth: "100%",
  },

  headerTitleWrap: {
    minWidth: 0,
    flex: 1,
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },

  tooltip: {
    fontSize: "0.9rem",
    padding: "12px 16px",
    maxWidth: 320,
  },

  arrow: {},

  /**
   * Botón circular de ayuda, con caja fija y centrado real.
   */
  infoIconWrapper: {
    width: 30,
    height: 30,
    padding: 0,
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.16)",
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.24)",
    },
  },

  /**
   * Ajuste fino para que la "i" no quede baja dentro del círculo.
   */
  infoIcon: {
    width: 18,
    height: 18,
    fontSize: 18,
    display: "block",
    position: "relative",
    top: "-2px",
  },

  cardBody: {
    padding: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },

  column: {
    width: "100%",
  },

  columnTitle: {
    marginBottom: theme.spacing(1.5),
    fontWeight: 600,
    color: "#3C4858",
    wordBreak: "break-word",
  },

  infoRow: {
    marginBottom: theme.spacing(1.25),
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    lineHeight: 1.55,
  },

  infoLabel: {
    fontWeight: 600,
    marginRight: theme.spacing(0.5),
  },

  findingCard: {
    padding: theme.spacing(1.25, 0),
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },

  findingRow: {
    marginBottom: theme.spacing(0.75),
    lineHeight: 1.55,
  },

  descriptionBlock: {
    marginTop: theme.spacing(0.5),
    whiteSpace: "pre-line",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },

  verticalDivider: {
    height: "100%",
    margin: theme.spacing(0, 1.5),

    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  sectionDivider: {
    margin: theme.spacing(1, 0),
  },

  emptyText: {
    textAlign: "center",
    padding: theme.spacing(1, 0),
    wordBreak: "break-word",
  },
}));

const HEADER_INFO = {
  certificados: "customtable-tooltip-certificados",
};

/**
 * Convierte el bloque certificate_info en pares clave/valor.
 * Soporta líneas con varios ":" sin perder el resto del texto.
 */
const parseCertificateInfo = (certificateInfo) => {
  if (!certificateInfo || typeof certificateInfo !== "string") {
    return [];
  }

  return certificateInfo
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separatorIndex = line.indexOf(":");

      if (separatorIndex === -1) {
        return {
          label: line,
          value: "",
        };
      }

      return {
        label: line.slice(0, separatorIndex).trim(),
        value: line.slice(separatorIndex + 1).trim(),
      };
    });
};

const CertificateInfo = ({ record, infoKey }) => {
  const { t } = useTranslation("search");
  const classes = useStyles();
  const customClasses = useCustomStyles();
  const mergedClasses = { ...classes, ...customClasses };

  const certificateInfo =
    record &&
    record.certificate_analysis &&
    record.certificate_analysis.certificate_info
      ? record.certificate_analysis.certificate_info
      : "";

  const certificateFindings =
    record &&
    record.certificate_analysis &&
    Array.isArray(record.certificate_analysis.certificate_findings)
      ? record.certificate_analysis.certificate_findings
      : [];

  const parsedInfo = parseCertificateInfo(certificateInfo);

  /**
   * Si no hay datos útiles, no renderizamos la card.
   * Mantiene el dashboard limpio.
   */
  if (parsedInfo.length === 0 && certificateFindings.length === 0) {
    return null;
  }

  const tooltipKey = infoKey && HEADER_INFO[infoKey] ? HEADER_INFO[infoKey] : null;
  const tooltipText = tooltipKey ? t(tooltipKey, { defaultValue: "" }) : "";

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary" text>
            <CardText color="primary" className={classes.headerBadge}>
              <div className={classes.headerContent}>
                <div className={classes.headerTitleWrap}>
                  <h4 className={mergedClasses.cardTitle} style={{ margin: 0 }}>
                    {t("category-title-certificates")}
                  </h4>
                </div>

                {tooltipText ? (
                  <div className={classes.headerActions}>
                    <InfoTooltipButton
                      title={tooltipText}
                      placement="right"
                      tooltipClasses={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                      iconButtonClassName={classes.infoIconWrapper}
                      iconClassName={classes.infoIcon}
                    />
                  </div>
                ) : null}
              </div>
            </CardText>
          </CardHeader>

          <CardBody className={classes.cardBody}>
            <GridContainer spacing={3} alignItems="flex-start">
              <GridItem xs={12} md={6}>
                <div className={classes.column}>
                  <Typography variant="subtitle1" className={classes.columnTitle}>
                    {t("category-title-certificates")}
                  </Typography>

                  {parsedInfo.length > 0 ? (
                    parsedInfo.map((item, index) => (
                      <div key={`cert-info-${index}`} className={classes.infoRow}>
                        <span className={classes.infoLabel}>{item.label}:</span>
                        <span>{item.value || "-"}</span>
                      </div>
                    ))
                  ) : (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={classes.emptyText}
                    >
                      {t("metainfo-no-records")}
                    </Typography>
                  )}
                </div>
              </GridItem>

              <GridItem xs={12} md={6}>
                <div className={classes.column}>
                  <Typography variant="subtitle1" className={classes.columnTitle}>
                    Findings
                  </Typography>

                  {certificateFindings.length > 0 ? (
                    certificateFindings.map((item, index) => (
                      <React.Fragment key={`cert-finding-${index}`}>
                        <div className={classes.findingCard}>
                          <div className={classes.findingRow}>
                            <span className={classes.infoLabel}>Severity:</span>
                            <span>{item && item[0] ? item[0] : "-"}</span>
                          </div>

                          <div className={classes.findingRow}>
                            <span className={classes.infoLabel}>Description:</span>
                            <span>{item && item[1] ? item[1] : "-"}</span>
                          </div>

                          {item && item[2] ? (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              className={classes.descriptionBlock}
                            >
                              {item[2]}
                            </Typography>
                          ) : null}
                        </div>

                        {index < certificateFindings.length - 1 ? (
                          <Divider className={classes.sectionDivider} />
                        ) : null}
                      </React.Fragment>
                    ))
                  ) : (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={classes.emptyText}
                    >
                      {t("metainfo-no-records")}
                    </Typography>
                  )}
                </div>
              </GridItem>
            </GridContainer>
          </CardBody>

          <CardFooter />
        </Card>
      </GridItem>
    </GridContainer>
  );
};

CertificateInfo.propTypes = {
  record: PropTypes.object.isRequired,
  infoKey: PropTypes.string,
};

export default CertificateInfo;